'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import justifiedLayout from 'justified-layout';
import { memo, type ReactNode, type RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

export type JustifiedGridItem = {
  /** Unique identifier for the item */
  id: string;
  /** Aspect ratio (width / height) */
  aspectRatio: number;
};

export type JustifiedGridProps<Item extends JustifiedGridItem> = {
  /** Additional height per row for content below thumbnails (e.g., metadata) */
  additionalRowHeight?: number;
  /** Gap between items in pixels */
  gap: number;
  /** Array of items with aspect ratios */
  items: Item[];
  /** Render function for each item */
  renderItem: (params: { height: number; index: number; item: Item; width: number }) => ReactNode;
  /**
   * When provided, only rows within the scroll element's visible viewport are rendered.
   * Pass the scroll container element (via useState + callback ref, not useRef) so the
   * virtualizer activates only after the element is mounted.
   */
  scrollElement?: HTMLElement | null;
  /** Target row height in pixels (thumbnail only, not including additionalRowHeight) */
  targetRowHeight: number;
};

/** A group of items that share the same vertical position (one visual row) */
type Row = { height: number; indices: number[]; top: number };

/**
 * Tracks the grid's pixel offset from the scroll container's top content edge.
 * Uses a MutationObserver on the scroll container so that DOM changes in sibling
 * sections (e.g. a boards section above the grid) are detected and the margin
 * is re-measured, keeping the virtualizer's row positions accurate.
 */
const useScrollMargin = (containerRef: RefObject<HTMLDivElement | null>, scrollElement: HTMLElement | null): number => {
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !scrollElement) {
      return;
    }

    const update = () => setScrollMargin(container.offsetTop);
    update();

    const obs = new MutationObserver(update);
    obs.observe(scrollElement, { attributes: true, childList: true, subtree: true });
    window.addEventListener('resize', update);

    return () => {
      obs.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [containerRef, scrollElement]);

  return scrollMargin;
};

/**
 * Justified grid layout (Flickr/Google Photos style).
 * - Fixed row height across all rows
 * - Variable item widths based on aspect ratio
 * - Rows fill container width exactly
 * - Supports additional height per row for metadata below thumbnails
 * - When scrollElement is provided, uses @tanstack/react-virtual to render only visible rows
 */
const _JustifiedGrid = <Item extends JustifiedGridItem>({
  additionalRowHeight = 0,
  gap,
  items,
  renderItem,
  scrollElement,
  targetRowHeight,
}: JustifiedGridProps<Item>) => {
  const { ref, width = 0 } = useResizeDetector<HTMLDivElement>();
  const scrollMargin = useScrollMargin(ref, scrollElement ?? null);

  // Calculate justified layout boxes
  const { boxes, containerHeight } = useMemo(() => {
    if (!width || items.length === 0) {
      return { boxes: [], containerHeight: 0 };
    }

    const result = justifiedLayout(
      items.map((item) => item.aspectRatio),
      {
        boxSpacing: {
          horizontal: gap,
          vertical: gap + additionalRowHeight,
        },
        containerPadding: 0,
        containerWidth: width,
        targetRowHeight,
      },
    );

    return {
      boxes: result.boxes,
      containerHeight: result.containerHeight,
    };
  }, [additionalRowHeight, gap, items, targetRowHeight, width]);

  // Group boxes into rows for row-level virtualization (same box.top = same row)
  const rows = useMemo<Row[]>(() => {
    if (!scrollElement || boxes.length === 0) {
      return [];
    }

    const rowMap = new Map<number, Row>();
    boxes.forEach((box, i) => {
      const existing = rowMap.get(box.top);
      if (existing) {
        existing.indices.push(i);
      } else {
        rowMap.set(box.top, { height: box.height, indices: [i], top: box.top });
      }
    });
    return Array.from(rowMap.values()).sort((a, b) => a.top - b.top);
  }, [boxes, scrollElement]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    // estimateSize uses the actual computed row height from justifiedLayout (exact, not estimated)
    estimateSize: (i) => (rows[i]?.height ?? targetRowHeight) + additionalRowHeight + gap,
    getScrollElement: () => scrollElement ?? null,
    overscan: 3,
    scrollMargin,
  });

  const renderBox = useCallback(
    (itemIndex: number) => {
      const box = boxes[itemIndex];
      const item = items[itemIndex];
      if (!box || !item) {
        return null;
      }

      const totalHeight = box.height + additionalRowHeight;

      return (
        <div
          className="absolute"
          key={item.id}
          style={{
            height: totalHeight,
            left: box.left,
            // Use justifiedLayout's exact top coordinate — consistent with the absolute
            // container height and avoids any estimateSize drift from virtualRow.start.
            top: box.top,
            width: box.width,
          }}
        >
          {renderItem({ height: totalHeight, index: itemIndex, item, width: box.width })}
        </div>
      );
    },
    [additionalRowHeight, boxes, items, renderItem],
  );

  const renderedBoxes = useMemo(() => boxes.map((_, i) => renderBox(i)), [boxes, renderBox]);

  return (
    <div className="w-full" ref={ref}>
      <div className="relative" style={{ height: containerHeight + additionalRowHeight }}>
        {scrollElement
          ? rowVirtualizer.getVirtualItems().flatMap((vRow) => (rows[vRow.index]?.indices ?? []).map(renderBox))
          : renderedBoxes}
      </div>
    </div>
  );
};

export const JustifiedGrid = memo(_JustifiedGrid) as typeof _JustifiedGrid;
